/**
 * The cloud's status frames as the app holds them. See docs/rules/api_boundary.md.
 */

import { z } from 'zod';

import type {
    DeviceStatusChanged, DeviceStatusDto, IoStateChanged, IoStateDto, ResourceStatus,
    ServerStatusChanged, ServerStatusDto,
} from '@/types/notifications';
import {
    DEVICE_STATUS_KEY_PREFIX, DeviceStatusDtoSchema, IO_STATE_KEY_PREFIX, IoStateDtoSchema,
    NotificationFrameSchema, ResourceStatusCode, SERVER_STATUS_KEY_PREFIX, ServerStatusDtoSchema,
} from '@/types/notifications';

function getResourceStatus(code: ResourceStatusCode): ResourceStatus {
    switch (code) {
        case ResourceStatusCode.Offline:
            return 'offline';
        case ResourceStatusCode.Unauthorized:
            return 'unauthorized';
        case ResourceStatusCode.Online:
            return 'online';
        case ResourceStatusCode.Recording:
            return 'recording';
        case ResourceStatusCode.Unknown:
            return '';
    }
}

export function toDeviceStatus(dto: DeviceStatusDto): DeviceStatusChanged {
    return {
        type: 'deviceStatusChanged',
        deviceId: dto.key.slice(DEVICE_STATUS_KEY_PREFIX.length),
        status: getResourceStatus(dto.value),
    };
}

export function toServerStatus(dto: ServerStatusDto): ServerStatusChanged {
    return {
        type: 'serverStatusChanged',
        serverId: dto.key.slice(SERVER_STATUS_KEY_PREFIX.length),
        status: getResourceStatus(dto.value),
    };
}

export function toIoState(dto: IoStateDto): IoStateChanged {
    return {
        type: 'ioStateChanged',
        deviceId: dto.key.slice(IO_STATE_KEY_PREFIX.length),
        ports: dto.value.ports,
    };
}

/** What the cloud's stream carries: its status frames, which have no type, and the typed ones. */
export const CloudFrameSchema = z.union([
    DeviceStatusDtoSchema.transform(toDeviceStatus),
    ServerStatusDtoSchema.transform(toServerStatus),
    IoStateDtoSchema.transform(toIoState),
    NotificationFrameSchema,
]);
